<script setup lang="ts">
import {useStorage} from "@vueuse/core";
import {useRoute, useRouter} from "vue-router";
import {saving} from "@/Composables/api";
import {Card, InputText, Label, Select, Skeleton, ToggleSwitch} from "@/Components/Library";
import {PageHeading} from "@/Components/Global";
import {FormButtons} from "@/Components/Forms";
import {useMainStore} from "@/Stores/store";
import {useToast} from "primevue/usetoast";
import {AiPromptCategoryOptions} from "@/Enums";
import {getAiPrompt, storeAiPrompt, updateAiPrompt} from "@/Services/Admin/admin-ai-prompts";
import {MilkdownProvider} from "@milkdown/vue";
import MilkdownEditor from "@/Pages/Admin/AiPrompts/MilkdownEditor.vue";
import {ref, useTemplateRef} from "vue";

const $store = useMainStore();
const toast = useToast();
const currentRoute = useRoute();
const router = useRouter();
const defaultForm = {
    id: null,
    title: "",
    content: "",
    category: null,
    is_active: false,
};

const aiPrompt = useStorage("admin-prompt-form", defaultForm);
const loading = ref(true);

if (currentRoute.params.aiPrompt) {
    getAiPrompt(currentRoute.params.aiPrompt)
        .then(({data}) => {
            const formData = {
                ...data,
                category: data.category?.value ?? null,
            };
            Object.assign(aiPrompt.value, formData);
        })
        .finally(() => {
            loading.value = false;
        });
} else if (aiPrompt.value.id) {
    aiPrompt.value = defaultForm;
    loading.value = false;
}

const save = async () => {
    aiPrompt.value.content = milkdown.value?.editor?.getMarkdown() ?? aiPrompt.value.content;
    try {
        if (currentRoute.params.aiPrompt) {
            await updateAiPrompt(aiPrompt.value);
        } else {
            await storeAiPrompt(aiPrompt.value).then(({data}) => {
                Object.assign(aiPrompt.value, {
                    ...data,
                    category: data.category?.value ?? null,
                });
                router.push({name: "admin.ai-prompts.edit", params: {aiPrompt: data.id}});
            });
        }

        toast.add({
            severity: "success",
            summary: "Success",
            detail: "AI Prompt saved successfully",
            life: 3000,
        });
    } catch (error) {
        console.error(error);
    }
};
</script>
<template>
    <div class="mx-auto w-full max-w-5xl pb-12">
        <div class="m-container mb-3">
            <PageHeading :title="`${currentRoute.params.aiPrompt ? 'Edit' : 'Create'} AI Prompt`" />
        </div>
        <form class="flex flex-col gap-4" @submit.prevent="save">
            <Card>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-bold">Details</h3>
                        <div class="flex items-center gap-2">
                            <Label for="is_active">Active</Label>
                            <ToggleSwitch input-id="is_active" name="is_active" v-model="aiPrompt.is_active" />
                        </div>
                    </div>
                </template>
                <div class="mt-2 flex flex-col gap-4">
                    <div class="">
                        <Label for="title" class="mb-1" required>Title</Label>
                        <Skeleton v-if="loading" height="3rem" class="mb-1"></Skeleton>
                        <InputText
                            v-else
                            id="title"
                            name="title"
                            placeholder="Title"
                            v-model="aiPrompt.title"
                            required
                            fluid
                        />
                    </div>
                    <div class="flex flex-col gap-2">
                        <Label for="category">Category</Label>
                        <Select
                            v-model="aiPrompt.category"
                            :options="Object.values(AiPromptCategoryOptions)"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select a category"
                            showClear
                            fluid
                        />
                    </div>
                    <div class="flex flex-col">
                        <Label for="content" class="mb-1">Content</Label>
                        <Skeleton v-if="loading" height="20rem" class="mb-2"></Skeleton>
                        <MilkdownProvider v-else>
                            <MilkdownEditor ref="milkdown" v-model="aiPrompt.content" />
                        </MilkdownProvider>
                    </div>
                </div>
            </Card>
            <FormButtons :show-cancel="false" :loading="!!saving" />
        </form>
    </div>
</template>
<style scoped></style>
